<template>
  <v-dialog :value="value" max-width="1000px" persistent>
    <v-card class="pa-4" outlined>
      <div class="d-flex py-2">
        <v-spacer></v-spacer>
        <h3>Vincular instituições em lote</h3>
        <v-spacer></v-spacer>
        <v-btn icon absolute top right :disabled="loading" @click="close">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </div>
      <v-divider class="my-4"></v-divider>

      <div class="text-center mb-2">
        <div class="grey--text text--darken-1 text-body-2 mb-2">
          {{ userIds.length }}
          {{
            userIds.length === 1
              ? 'usuário selecionado'
              : 'usuários selecionados'
          }}
        </div>
        <v-chip
          v-for="selected in visibleUsers"
          :key="selected.id"
          color="#0F3565"
          text-color="#fff"
          small
          class="ma-1"
        >
          {{ selected.name }}
        </v-chip>
        <v-chip
          v-if="userIds.length > visibleUsers.length"
          small
          class="ma-1"
          color="grey lighten-2"
        >
          +{{ userIds.length - visibleUsers.length }}
        </v-chip>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex justify-center">
        <v-btn-toggle v-model="mode" mandatory rounded color="#0F3565">
          <v-btn value="uniform" small :disabled="loading">
            <v-icon left small>mdi-account-multiple-check</v-icon>
            Mesmo acesso para todos
          </v-btn>
          <v-btn value="perUser" small :disabled="loading || !canConfigurePerUser">
            <v-icon left small>mdi-account-cog</v-icon>
            Configurar por usuário
          </v-btn>
        </v-btn-toggle>
      </div>

      <div
        v-if="mode === 'perUser' && !canConfigurePerUser"
        class="text-center grey--text text--darken-1 text-caption mt-2"
      >
        Não foi possível carregar os dados de todos os usuários selecionados.
      </div>

      <v-alert
        v-if="results.length > 0"
        dense
        text
        :type="hasFailures ? 'warning' : 'success'"
        class="text-body-2 mt-4 mb-0 rounded-lg"
      >
        <div
          v-for="(result, i) in results"
          :key="`${result.userId}-${result.institutionId}-${i}`"
        >
          <v-icon small :color="result.success ? 'green' : 'red'" class="mr-1">
            {{ result.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
          </v-icon>
          <b>{{ result.userName || 'Usuário' }}</b>
          <span v-if="result.institutionName">
            &rarr; {{ result.institutionName }}
          </span>
          : {{ result.message }}
        </div>
      </v-alert>

      <div style="max-height: 500px; overflow-y: auto">
        <div v-for="(group, g) in groups" :key="group.key">
          <div v-if="mode === 'perUser'" class="d-flex align-center px-3 mt-3">
            <v-icon small color="#0F3565" class="mr-2">mdi-account</v-icon>
            <b>{{ group.name }}</b>
            <span class="grey--text text--darken-1 text-body-2 ml-2">
              {{ group.email }}
            </span>
          </div>

          <v-row class="ma-0">
            <v-col cols="12" sm="6" md="4">
              <v-card
                class="pa-4 d-flex flex-column"
                outlined
                rounded="lg"
                height="100%"
              >
                <v-card-text class="pa-0">
                  <div class="d-flex justify-center mb-2">
                    <v-icon size="30" color="grey darken-1">
                      mdi-domain-plus
                    </v-icon>
                  </div>
                  <p class="text-center" style="word-break: keep-all">
                    <b>Adicionar instituição</b>
                  </p>

                  <v-spacer></v-spacer>

                  <label for="institution">Instituição</label>
                  <v-autocomplete
                    v-model="group.institutionToAdd"
                    :items="availableInstitutions(group)"
                    item-text="name"
                    item-value="id"
                    placeholder="Selecione a instituição"
                    hide-details="auto"
                    height="45"
                    append-icon="mdi-chevron-down"
                    :disabled="loading"
                    :loading="institutionLoading"
                    outlined
                    background-color="#F5F5F5"
                    class="rounded-lg mb-2"
                  ></v-autocomplete>

                  <v-btn
                    class="ma-0 mt-4 secondary-button"
                    depressed
                    block
                    :disabled="!group.institutionToAdd"
                    :loading="institutionLoading || loading"
                    @click="addAccess(group)"
                  >
                    Adicionar Acesso
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-col>

            <v-col
              v-for="(access, i) in group.accesses"
              :key="access.institutionId"
              cols="12"
              sm="6"
              md="4"
            >
              <v-card
                class="pa-4 d-flex flex-column"
                outlined
                rounded="lg"
                height="100%"
              >
                <div>
                  <div class="d-flex justify-center">
                    <v-avatar v-if="access.logoPath" size="40" class="mr-2">
                      <v-img :src="access.logoPath" contain></v-img>
                    </v-avatar>
                    <p v-else>
                      <v-icon size="30" color="grey darken-1">mdi-domain</v-icon>
                    </p>
                  </div>

                  <p class="text-center" style="word-break: keep-all">
                    <b>{{ access.name }}</b>
                  </p>
                </div>

                <v-spacer></v-spacer>

                <v-form :ref="`batch-access-form` + g + `-` + i">
                  <div>
                    <label for="userType">Nível de acesso</label>
                    <v-select
                      v-model="access.userType"
                      :items="types"
                      item-text="text"
                      item-value="value"
                      placeholder="Selecione o nível de acesso"
                      :rules="[
                        (v) => v !== null || 'Nível de acesso é obrigatório',
                      ]"
                      hide-details="auto"
                      height="45"
                      append-icon="mdi-chevron-down"
                      :disabled="loading"
                      outlined
                      background-color="#F5F5F5"
                      class="rounded-lg mb-2"
                    >
                      <template v-slot:item="{ item }">
                        <v-tooltip right max-width="300">
                          <template v-slot:activator="{ on, attrs }">
                            <v-list-item-content v-bind="attrs" v-on="on">
                              <v-list-item-title>
                                {{ item.text }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </template>
                          <span>{{ item.tooltip }}</span>
                        </v-tooltip>
                      </template>
                    </v-select>
                  </div>

                  <div>
                    <label for="unidades">Unidades</label>
                    <v-select
                      v-model="access.unidades"
                      :items="unidadesByInstitution[access.institutionId] || []"
                      multiple
                      item-text="name"
                      item-value="id"
                      placeholder="Selecione a unidade"
                      hide-details="auto"
                      append-icon="mdi-chevron-down"
                      :disabled="loading"
                      :loading="access.unidadeLoading"
                      outlined
                      background-color="#F5F5F5"
                      class="rounded-lg mb-2"
                    >
                      <template
                        v-slot:selection="{
                          attrs,
                          item,
                          select,
                          selected,
                          index,
                        }"
                      >
                        <v-chip
                          v-if="index === 0"
                          v-bind="attrs"
                          :input-value="selected"
                          color="#BBCF20"
                          text-color="#fff"
                          small
                          @click="select"
                        >
                          {{ item.name }}
                        </v-chip>
                        <span
                          v-if="access.unidades && index === 1"
                          class="grey--text text-caption"
                          style="font-size: 0.8rem"
                        >
                          ( +{{ access.unidades.length - 1 }})
                        </span>
                      </template>
                    </v-select>
                  </div>

                  <div>
                    <label for="admissionDate">Data de admissão</label>
                    <v-menu
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="access.admissionDateAux"
                          v-mask="['##/##/####']"
                          placeholder="Selecione a data de admissão"
                          prepend-inner-icon="mdi-calendar"
                          :rules="[
                            (v) => !!v || 'Data de admissão é obrigatória',
                            () =>
                              !access.admissionDateAux ||
                              !!parseDate(access.admissionDateAux) ||
                              'Data de admissão inválida ou futura',
                          ]"
                          hide-details="auto"
                          :disabled="loading"
                          @blur="
                            access.admissionDate = parseDate(
                              access.admissionDateAux
                            )
                          "
                          v-bind="attrs"
                          v-on="on"
                          outlined
                          background-color="#F5F5F5"
                          class="rounded-lg mb-2"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="access.admissionDate"
                        :max="today"
                        min="1900-01-01"
                        no-title
                        @change="setAdmissionDate(access, $event)"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-form>

                <v-spacer></v-spacer>

                <v-btn
                  block
                  color="error"
                  max-height="40"
                  depressed
                  class="my-1 rounded-lg"
                  :disabled="loading"
                  @click="removeAccess(group, i)"
                >
                  Remover
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </div>

      <v-divider class="my-4"></v-divider>

      <div class="d-flex">
        <v-spacer></v-spacer>
        <v-btn
          class="ma-0 secondary-button"
          depressed
          :disabled="!hasAnyAccess"
          :loading="loading"
          @click="submit"
        >
          Vincular
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import ApiService from '../../../../services/ApiService'

export default {
  name: 'BatchInstitutionAccess',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // Ids of every selected user, including those on other pages of the table.
    userIds: {
      type: Array,
      required: true,
    },
    // Data of every selected user, kept by the table as each one is selected,
    // so it covers selections made across other filters and pages.
    users: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    apiService: new ApiService(),
    loading: false,
    institutionLoading: false,
    institutions: [],
    unidadesByInstitution: {},
    results: [],
    mode: 'uniform',
    uniformGroup: {
      key: 'uniform',
      userId: null,
      institutionToAdd: null,
      accesses: [],
    },
    perUserGroups: [],
    types: [
      {
        text: 'Supervisor Principal',
        value: 8,
        tooltip:
          'Acesso total ao sistema, incluindo configurações administrativas e gestão de usuários.',
      },
      {
        text: 'Supervisor',
        value: 2,
        tooltip:
          'Acesso amplo à operação da instituição. Pode criar, editar, inativar equipamentos e gerenciar todos os módulos operacionais, relatórios, usuários, chat e alertas.',
      },
      {
        text: 'Operador',
        value: 3,
        tooltip:
          'Execução técnica. Acessa os principais módulos operacionais, mas sem funções administrativas como gestão de usuários, configurações do sistema ou alertas da barra de topo.',
      },
      {
        text: 'Coordenador Op.',
        value: 4,
        tooltip:
          'Coordenação operacional com acesso em modo visualização. Botões de criar, editar e fechar estão desabilitados na maioria das páginas. Não acessa Big Data, Usuários nem Repositório de Arquivos.',
      },
      {
        text: 'Coordenador Setor Assistencial',
        value: 5,
        tooltip:
          'Perfil voltado ao setor clínico/assistencial. Acesso restrito a Equipamentos, OS, Chamados, Manutenção Programada, Dashboard e Agendamentos. Não acessa Estoque, Relatórios completos, Big Data, Usuários nem módulos administrativos.',
      },
      {
        text: 'Solicitador',
        value: 6,
        tooltip:
          'Perfil mais restrito. Destinado a quem apenas abre chamados. Não acessa Equipamentos, OS, Manutenção, Estoque, Relatórios, Big Data, Chat nem Alertas.',
      },
    ],
  }),
  computed: {
    today() {
      return moment().format('YYYY-MM-DD')
    },
    hasFailures() {
      return this.results.some((result) => !result.success)
    },
    // Caps the chip list so a large selection does not flood the header.
    visibleUsers() {
      return this.users.slice(0, 8)
    },
    // Per-user mode needs each user's data to label its block.
    canConfigurePerUser() {
      return this.users.length === this.userIds.length && this.users.length > 0
    },
    groups() {
      return this.mode === 'perUser' ? this.perUserGroups : [this.uniformGroup]
    },
    hasAnyAccess() {
      return this.groups.some((group) => group.accesses.length > 0)
    },
  },
  watch: {
    mode(value) {
      if (value === 'perUser') this.buildPerUserGroups()
    },
  },
  created() {
    this.getInstitutions()
  },
  methods: {
    // Seeds one block per user, carrying over whatever was already set in the
    // uniform tab. Blocks the user already edited are kept untouched.
    buildPerUserGroups() {
      this.perUserGroups = this.users.map((user) => {
        const existing = this.perUserGroups.find(
          (group) => group.userId === user.id
        )
        if (existing) return existing

        return {
          key: user.id,
          userId: user.id,
          name: user.name,
          email: user.email,
          institutionToAdd: null,
          accesses: this.uniformGroup.accesses.map((access) => ({
            ...access,
            unidades: [...access.unidades],
          })),
        }
      })
    },
    // Hides institutions already staged in this group.
    availableInstitutions(group) {
      const staged = group.accesses.map((access) => access.institutionId)
      return this.institutions.filter(
        (institution) => !staged.includes(institution.id)
      )
    },
    addAccess(group) {
      const institution = this.institutions.find(
        (item) => item.id === group.institutionToAdd
      )
      if (!institution) return

      const access = {
        institutionId: institution.id,
        name: institution.name,
        logoPath: institution.logoPath,
        userType: null,
        admissionDate: null,
        admissionDateAux: null,
        unidades: [],
        unidadeLoading: false,
      }

      group.accesses.push(access)
      group.institutionToAdd = null
      this.getUnidades(access)
    },
    setAdmissionDate(access, date) {
      access.admissionDateAux = moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },
    // Returns YYYY-MM-DD, or null when the text is not a real date or is in the future.
    parseDate(text) {
      const date = moment(text, 'DD/MM/YYYY', true)
      return date.isValid() && date.isSameOrBefore(moment(), 'day')
        ? date.format('YYYY-MM-DD')
        : null
    },
    removeAccess(group, index) {
      group.accesses.splice(index, 1)
    },
    getInstitutions() {
      this.institutionLoading = true
      this.apiService
        .get('institution/list')
        .then((response) => {
          this.institutions = response.content || []
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar instituições')
        })
        .finally(() => {
          this.institutionLoading = false
        })
    },
    getUnidades(access) {
      if (this.unidadesByInstitution[access.institutionId]) return

      access.unidadeLoading = true
      this.apiService
        .get(`unidade/list/${access.institutionId}`)
        .then((response) => {
          this.$set(
            this.unidadesByInstitution,
            access.institutionId,
            response.content || []
          )
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar unidades')
        })
        .finally(() => {
          access.unidadeLoading = false
        })
    },
    validate() {
      return this.groups.every((group, g) =>
        group.accesses.every((access, i) => {
          const form = this.$refs[`batch-access-form` + g + `-` + i]
          return form ? (Array.isArray(form) ? form[0] : form).validate() : true
        })
      )
    },
    buildPayload() {
      const mapAccesses = (accesses) =>
        accesses.map((access) => ({
          institutionId: access.institutionId,
          userType: access.userType,
          admissionDate: access.admissionDate,
          unidades: access.unidades.map((id) => ({ unidadeId: id })),
        }))

      if (this.mode === 'perUser') {
        return {
          users: this.perUserGroups
            .filter((group) => group.accesses.length > 0)
            .map((group) => ({
              userId: group.userId,
              accesses: mapAccesses(group.accesses),
            })),
        }
      }

      return {
        userIds: this.userIds,
        accesses: mapAccesses(this.uniformGroup.accesses),
      }
    },
    submit() {
      if (!this.hasAnyAccess) return
      if (!this.validate()) return

      this.loading = true
      this.results = []

      this.apiService
        .post('user/addAccessBatch', this.buildPayload())
        .then((response) => {
          const content = response.content || response
          this.results = content.results || []

          if (this.hasFailures) {
            // Keeps the dialog open so the user can read, per user and
            // institution, exactly which links failed and why.
            this.$emit('refresh')
            this.$toast.warning(content.message)
          } else {
            this.$toast.success(content.message)
            this.$emit('saved')
            this.close()
          }
        })
        .catch((error) => {
          // 403 is returned by the API without a body, so it needs its own message.
          if (error?.status === 403) {
            this.$toast.error(
              'Apenas usuários Master e Administrador podem vincular instituições em lote.'
            )
            return
          }

          const content = error?.body?.content
          this.results = content?.results || []
          this.$toast.error(
            content?.message ||
              error?.body?.message ||
              'Erro ao vincular instituições'
          )
        })
        .finally(() => {
          this.loading = false
        })
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>
